import React, { useEffect, useState } from "react";
import ToolbarHeader from "../../components/ToolbarHeader";
import CustomButton from "../../components/CustomButton";
import { icons } from "../../assets/icons";
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import CustomTable from "../../components/CustomTable";
import DatePicker from "../../components/FormFields/DatePicker";
import moment from "moment";
import useApi from "../../hooks/useApi";
import { apiUrls } from "../../utils/api/apiUrls";
import { useAuth } from "../../contexts/AuthContext";
import PreviewDocModal from "./PreviewDocModal";
import { displayDateFormat, getArrayOfSelectedStoresCode } from "../../utils/helpers";
import { normalizeCashDepositListResponse } from "../../utils/dmsStandaloneListUtils";

const CashDeposits = () => {
  const { data = [], apiCall } = useApi();
  const {
    user: { userLabel = "" },
    setDownloadPreviewOpenWithData,
    downloadPreviewOpenWithData,
    selectedStores,
    storeDropdownState,
  } = useAuth();
  const { apiCall: fileDownload } = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [startDate, setStartDate] = useState(
    moment(
      searchParams.get("startDate") || moment().subtract({ days: 7 }).toDate()
    ).toDate()
  );
  const [endDate, setEndDate] = useState(
    moment(searchParams.get("endDate") || new Date()).toDate()
  );
  const onClickSubmitCashDeposit = () => {
    navigate("/cash-deposit/submit");
  };

  const cashDepositApiCall = (start, end) => {
    const nextSearch = createSearchParams({
      startDate: start,
      endDate: end,
    }).toString();
    const currentSearch = window.location.search?.startsWith("?")
      ? window.location.search.slice(1)
      : window.location.search || "";
    if (currentSearch !== nextSearch) {
      navigate(
        {
          pathname: window.location.pathname,
          search: nextSearch,
        },
        { replace: true }
      );
    }

    apiCall({
      url: apiUrls.dmsService.getRecords,
      dataToSend: {
        startDate: moment(start).startOf("d").format("YYYY-MM-DD HH:mm:ss"),
        metadataSchemaId: 7,
        endDate: moment(end || start)
          .endOf("d")
          .format("YYYY-MM-DD HH:mm:ss"),
        searchText: "",
        status: null,
        storeCodes: getArrayOfSelectedStoresCode(
          selectedStores,
          storeDropdownState
        ),
      },
      method: "post",
      processData: (raw) => normalizeCashDepositListResponse(raw),
    });
  };

  useEffect(() => {
    cashDepositApiCall(startDate, endDate);
  }, [selectedStores, storeDropdownState, location.key]);

  const onChangeDate = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    if (end) cashDepositApiCall(start, end);
  };

  const onClickDownloadFile = (file_id, filename) => {
    fileDownload({
      url: apiUrls.dmsService.getFile + "/" + file_id,
      method: "get",
      responseType: "blob",
      onSuccess: (apiData) => {
        const url = window.URL.createObjectURL(new Blob([apiData.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      },
    });
  };

  const onClickViewFile = (file_id, filename) => {
    fileDownload({
      url: apiUrls.dmsService.getFile + "/" + file_id,
      method: "get",
      responseType: "blob",
      onSuccess: (apiData) => {
        setDownloadPreviewOpenWithData({
          file: apiData.data,
          filename,
          isOpen: true,
        });
      },
    });
  };

  const columns = [
    {
      Header: "Date",
      accessor: "sales_day",
      Cell: (props) => {
        const value = moment(props?.row?.values?.sales_day).format(displayDateFormat);
        return <div className="">{value}</div>;
      },
    },
    {
      Header: "Store Code",
      accessor: "storeCode",
    },
    {
      Header: "Description",
      accessor: "comment",
    },
    {
      Header: "Total Sales",
      accessor: "total_cash_sales",
      Cell: (props) => {
        const value = props?.row?.original?.total_cash_sales;
        return value ? Number(value).toFixed(2) : "-";
      },
    },
    {
      Header: "Cash Deposit",
      accessor: "cash_deposit",
      Cell: (props) => {
        const value = props?.row?.original?.cash_deposit;
        return value ? Number(value).toFixed(2) : "-";
      },
    },
    {
      Header: "",
      accessor: "id",
      Cell: (props) => {
        if (!props?.row?.original?.file_id) {
          return null;
        }

        return (
          <div className="flex">
            <img
              src={icons.squareEyeIcon}
              alt="view icon"
              className="cursor-pointer"
              onClick={() => {
                onClickViewFile(
                  props?.row?.original?.file_id,
                  props?.row?.original?.filename
                );
              }}
            />
            <img
              src={icons.downloadIcon}
              alt="attachment icon"
              className="w-[2.1rem] h-[1.9rem] cursor-pointer ml-4"
              onClick={() => {
                onClickDownloadFile(
                  props?.row?.original?.file_id,
                  props?.row?.original?.filename
                );
              }}
            />
          </div>
        );
      },
    },
  ];

  const cashDepositsData = Array.isArray(data) ? data : [];

  return (
    <div className="dashboard-container">
      <ToolbarHeader
        title="Cash Deposit"
        showBack={false}
        containerClassName="mt-14"
        renderRight={() => {
          if (userLabel !== "Store") {
            return null;
          }
          return (
            <CustomButton
              title="Submit Cash Deposit"
              icon={icons.plusIcon}
              onClick={onClickSubmitCashDeposit}
            />
          );
        }}
      />
      <div className="flex flex-col bg-[var(--whiteColor)] rounded-[1rem] mt-8 px-4 py-7">
        <div className="flex items-center gap-x-3 self-end mb-8">
          <div className="font-size-medium">Date Range</div>
          <DatePicker
            startDate={startDate}
            endDate={endDate}
            onChange={onChangeDate}
            maxDate={new Date()}
            dateRange
          />
        </div>
        <CustomTable
          data={cashDepositsData}
          columns={columns}
          centerAlignedColumns={("total_sales", "cash_deposit")}
        />
      </div>
      <PreviewDocModal
        isModalVisible={downloadPreviewOpenWithData}
        setIsModalVisible={setDownloadPreviewOpenWithData}
      />
    </div>
  );
};

export default CashDeposits;
